import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch, faTimes, faBuilding, faUser, faBriefcase,
    faBullseye, faCoins, faUsers, faSpinner, faArrowRight,
    faExternalLinkAlt, faChartLine
} from '@fortawesome/free-solid-svg-icons';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import AccountDetailModal from './AccountDetailModal';

function GlobalSearch() {
    const navigate = useNavigate();
    const { getRbacParams } = useAuth();

    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('ALL');

    // Cached raw data
    const [accounts, setAccounts] = useState([]);
    const [opportunities, setOpportunities] = useState([]);
    const [users, setUsers] = useState([]);
    const [dataFetched, setDataFetched] = useState(false);

    // Modal state for viewing account directly from search
    const [modalAccount, setModalAccount] = useState(null);
    const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);

    const searchRef = useRef(null);
    const inputRef = useRef(null);

    // ⌨️ Keyboard shortcut: Command+K or Ctrl+K
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                inputRef.current?.focus();
                setIsOpen(true);
            } else if (e.key === 'Escape') {
                setIsOpen(false);
                inputRef.current?.blur();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // 📥 Fetch searchable records when opened
    const fetchData = async () => {
        if (dataFetched) return;
        try {
            setLoading(true);
            const rbac = getRbacParams();
            const qstr = new URLSearchParams(rbac).toString();

            const [leadsRes, oppsRes, usersRes] = await Promise.all([
                api.get(`/leads?${qstr}`).catch(() => ({ data: [] })),
                api.get(`/opportunities?${qstr}`).catch(() => ({ data: [] })),
                api.get('/users').catch(() => ({ data: [] }))
            ]);

            setAccounts(leadsRes.data || []);
            setOpportunities(oppsRes.data || []);
            setUsers(usersRes.data || []);
            setDataFetched(true);
        } catch (err) {
            console.error('Error prefetching search data:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleFocus = () => {
        setIsOpen(true);
        fetchData();
    };

    // 🔍 Search matching logic
    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) {
            return { accounts: [], contacts: [], opportunities: [], users: [], totalCount: 0 };
        }

        // 1. Matching Accounts
        const matchedAccounts = accounts.filter(a => {
            const name = (a.AccountName || '').toLowerCase();
            const city = (a.City || '').toLowerCase();
            const status = (a.Status || '').toLowerCase();
            return name.includes(q) || city.includes(q) || status.includes(q);
        });

        // 2. Matching Contacts
        const matchedContacts = accounts.filter(a => {
            const contact = (a.ContactPerson || '').toLowerCase();
            const desig = (a.ContactDesignation || '').toLowerCase();
            const phone1 = (a.CntPhone1 || '').toLowerCase();
            const email1 = (a.CntMail1 || '').toLowerCase();
            const phone2 = (a.CntPhone2 || '').toLowerCase();
            const email2 = (a.CntMail2 || '').toLowerCase();
            return contact.includes(q) || desig.includes(q) || phone1.includes(q) || email1.includes(q) || phone2.includes(q) || email2.includes(q);
        });

        // 3. Matching Opportunities
        const matchedOpps = opportunities.filter(o => {
            const name = (o.OpprName || '').toLowerCase();
            const acc = (o.AccountName || '').toLowerCase();
            const stage = (o.Stage || '').toLowerCase();
            const status = (o.Status || '').toLowerCase();
            const amt = String(o.Amount || '');
            return name.includes(q) || acc.includes(q) || stage.includes(q) || status.includes(q) || amt.includes(q);
        });

        // 4. Matching Users
        const matchedUsers = users.filter(u => {
            const name = (u.name || '').toLowerCase();
            const email = (u.email || '').toLowerCase();
            const desig = (u.designation || '').toLowerCase();
            return name.includes(q) || email.includes(q) || desig.includes(q);
        });

        const totalCount = matchedAccounts.length + matchedContacts.length + matchedOpps.length + matchedUsers.length;

        return {
            accounts: matchedAccounts,
            contacts: matchedContacts,
            opportunities: matchedOpps,
            users: matchedUsers,
            totalCount
        };
    }, [query, accounts, opportunities, users]);

    const handleSelectAccount = (acc) => {
        setModalAccount(acc);
        setIsAccountModalOpen(true);
        setIsOpen(false);
    };

    const handleSelectOpp = (opp) => {
        setIsOpen(false);
        navigate(`/opportunities/${opp.OprID}`);
    };

    const handleSelectUser = () => {
        setIsOpen(false);
        navigate('/users');
    };

    const handleClear = () => {
        setQuery('');
        inputRef.current?.focus();
    };

    return (
        <div className="slds-search-wrapper" ref={searchRef}>
            {/* SEARCH INPUT BAR */}
            <div className={`global-search-bar ${isOpen ? 'active-focus' : ''}`}>
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                <input
                    ref={inputRef}
                    type="text"
                    className="search-input"
                    placeholder="Search SalesVault accounts, contacts, opportunities (⌘K)..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={handleFocus}
                />
                {query && (
                    <button type="button" className="slds-search-clear-btn" onClick={handleClear} title="Clear search">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                )}
            </div>

            {/* RESULTS DROPDOWN POPOVER */}
            {isOpen && (
                <div className="slds-search-popover">

                    {/* CATEGORY FILTER TABS */}
                    {query.trim() && (
                        <div className="slds-search-tabs">
                            <button
                                type="button"
                                className={`slds-search-tab ${selectedCategory === 'ALL' ? 'active' : ''}`}
                                onClick={() => setSelectedCategory('ALL')}
                            >
                                All ({results.totalCount})
                            </button>
                            <button
                                type="button"
                                className={`slds-search-tab ${selectedCategory === 'ACCOUNTS' ? 'active' : ''}`}
                                onClick={() => setSelectedCategory('ACCOUNTS')}
                            >
                                Accounts ({results.accounts.length})
                            </button>
                            <button
                                type="button"
                                className={`slds-search-tab ${selectedCategory === 'CONTACTS' ? 'active' : ''}`}
                                onClick={() => setSelectedCategory('CONTACTS')}
                            >
                                Contacts ({results.contacts.length})
                            </button>
                            <button
                                type="button"
                                className={`slds-search-tab ${selectedCategory === 'OPPORTUNITIES' ? 'active' : ''}`}
                                onClick={() => setSelectedCategory('OPPORTUNITIES')}
                            >
                                Opportunities ({results.opportunities.length})
                            </button>
                            <button
                                type="button"
                                className={`slds-search-tab ${selectedCategory === 'USERS' ? 'active' : ''}`}
                                onClick={() => setSelectedCategory('USERS')}
                            >
                                Users ({results.users.length})
                            </button>
                        </div>
                    )}

                    {/* RESULTS BODY */}
                    <div className="slds-search-results-scroll">

                        {loading ? (
                            <div className="slds-search-loading">
                                <FontAwesomeIcon icon={faSpinner} className="fa-spin search-loading-icon" />
                                <span>Searching CRM records...</span>
                            </div>
                        ) : !query.trim() ? (
                            /* SUGGESTED SHORTCUTS / RECENT */
                            <div className="slds-search-shortcuts">
                                <div className="slds-search-section-title">Quick Navigation Shortcuts</div>
                                <div className="slds-shortcut-item" onClick={() => { setIsOpen(false); navigate('/dashboard'); }}>
                                    <div className="slds-shortcut-icon blue"><FontAwesomeIcon icon={faChartLine} /></div>
                                    <div className="slds-shortcut-text">
                                        <span className="title">Executive Dashboard</span>
                                        <span className="desc">KPI metrics, revenue targets, and stage breakdown</span>
                                    </div>
                                    <FontAwesomeIcon icon={faArrowRight} className="arrow-icon" />
                                </div>
                                <div className="slds-shortcut-item" onClick={() => { setIsOpen(false); navigate('/leads'); }}>
                                    <div className="slds-shortcut-icon purple"><FontAwesomeIcon icon={faBuilding} /></div>
                                    <div className="slds-shortcut-text">
                                        <span className="title">Accounts &amp; Leads Directory</span>
                                        <span className="desc">Manage client organizations and individual practitioners</span>
                                    </div>
                                    <FontAwesomeIcon icon={faArrowRight} className="arrow-icon" />
                                </div>
                                <div className="slds-shortcut-item" onClick={() => { setIsOpen(false); navigate('/contacts'); }}>
                                    <div className="slds-shortcut-icon green"><FontAwesomeIcon icon={faUser} /></div>
                                    <div className="slds-shortcut-text">
                                        <span className="title">Key Contacts &amp; Outreach</span>
                                        <span className="desc">Phone, email, and LinkedIn profiles for decision makers</span>
                                    </div>
                                    <FontAwesomeIcon icon={faArrowRight} className="arrow-icon" />
                                </div>
                                <div className="slds-shortcut-item" onClick={() => { setIsOpen(false); navigate('/opportunities'); }}>
                                    <div className="slds-shortcut-icon amber"><FontAwesomeIcon icon={faCoins} /></div>
                                    <div className="slds-shortcut-text">
                                        <span className="title">Opportunity Pipeline</span>
                                        <span className="desc">7-stage sales pipeline deals and win/loss tracking</span>
                                    </div>
                                    <FontAwesomeIcon icon={faArrowRight} className="arrow-icon" />
                                </div>
                                <div className="slds-shortcut-item" onClick={() => { setIsOpen(false); navigate('/targets'); }}>
                                    <div className="slds-shortcut-icon sky"><FontAwesomeIcon icon={faBullseye} /></div>
                                    <div className="slds-shortcut-text">
                                        <span className="title">Sales Targets &amp; Quotas</span>
                                        <span className="desc">Quarterly and half-year quota allocations</span>
                                    </div>
                                    <FontAwesomeIcon icon={faArrowRight} className="arrow-icon" />
                                </div>
                            </div>
                        ) : results.totalCount === 0 ? (
                            /* NO RESULTS FOUND */
                            <div className="slds-search-empty">
                                <FontAwesomeIcon icon={faSearch} className="search-empty-icon" />
                                <h4>No CRM records found for "{query}"</h4>
                                <p>Try searching with another keyword like an account name, contact person, or opportunity title.</p>
                            </div>
                        ) : (
                            /* RESULTS LIST */
                            <div className="slds-search-results-list">

                                {/* 1. ACCOUNTS */}
                                {(selectedCategory === 'ALL' || selectedCategory === 'ACCOUNTS') && results.accounts.length > 0 && (
                                    <div className="slds-result-group">
                                        <div className="slds-result-group-header">
                                            <FontAwesomeIcon icon={faBuilding} className="mr-6px text-secondary" />
                                            Accounts ({results.accounts.length})
                                        </div>
                                        {results.accounts.map(acc => (
                                            <div
                                                key={`acc-${acc.AccountID}`}
                                                className="slds-result-row"
                                                onClick={() => handleSelectAccount(acc)}
                                            >
                                                <div className="slds-result-icon purple">
                                                    <FontAwesomeIcon icon={faBuilding} />
                                                </div>
                                                <div className="slds-result-meta">
                                                    <div className="slds-result-main">
                                                        <span className="slds-result-title">{acc.AccountName}</span>
                                                        <span className={`slds-status-pill status-${(acc.Status || 'lead').toLowerCase()}`}>
                                                            {acc.Status || 'Lead'}
                                                        </span>
                                                        <span className={`slds-priority-pill priority-${(acc.Priority || 'medium').toLowerCase()}`}>
                                                            {acc.Priority || 'Medium'}
                                                        </span>
                                                    </div>
                                                    <div className="slds-result-sub">
                                                        <span>{acc.AccountType || 'Organization'}</span>
                                                        {acc.City && <span>&bull; {acc.City}</span>}
                                                        {acc.ContactPerson && <span>&bull; Rep: {acc.ContactPerson}</span>}
                                                    </div>
                                                </div>
                                                <button type="button" className="slds-result-action" title="View Account Details">
                                                    <span>View</span>
                                                    <FontAwesomeIcon icon={faExternalLinkAlt} className="text-10" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* 2. CONTACTS */}
                                {(selectedCategory === 'ALL' || selectedCategory === 'CONTACTS') && results.contacts.length > 0 && (
                                    <div className="slds-result-group">
                                        <div className="slds-result-group-header">
                                            <FontAwesomeIcon icon={faUser} className="mr-6px text-secondary" />
                                            Key Contacts ({results.contacts.length})
                                        </div>
                                        {results.contacts.map(cnt => (
                                            <div
                                                key={`cnt-${cnt.AccountID}`}
                                                className="slds-result-row"
                                                onClick={() => handleSelectAccount(cnt)}
                                            >
                                                <div className="slds-result-icon blue">
                                                    <FontAwesomeIcon icon={faUser} />
                                                </div>
                                                <div className="slds-result-meta">
                                                    <div className="slds-result-main">
                                                        <span className="slds-result-title">{cnt.ContactPerson || cnt.AccountName}</span>
                                                        {cnt.ContactDesignation && (
                                                            <span className="slds-role-tag">{cnt.ContactDesignation}</span>
                                                        )}
                                                    </div>
                                                    <div className="slds-result-sub">
                                                        <span>Account: {cnt.AccountName}</span>
                                                        {cnt.CntPhone1 && <span>&bull; 📞 {cnt.CntPhone1}</span>}
                                                        {cnt.CntMail1 && <span>&bull; ✉️ {cnt.CntMail1}</span>}
                                                    </div>
                                                </div>
                                                <button type="button" className="slds-result-action" title="View Contact Details">
                                                    <span>Details</span>
                                                    <FontAwesomeIcon icon={faExternalLinkAlt} className="text-10" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* 3. OPPORTUNITIES */}
                                {(selectedCategory === 'ALL' || selectedCategory === 'OPPORTUNITIES') && results.opportunities.length > 0 && (
                                    <div className="slds-result-group">
                                        <div className="slds-result-group-header">
                                            <FontAwesomeIcon icon={faBriefcase} className="mr-6px text-secondary" />
                                            Opportunities ({results.opportunities.length})
                                        </div>
                                        {results.opportunities.map(opp => (
                                            <div
                                                key={`opp-${opp.OprID}`}
                                                className="slds-result-row"
                                                onClick={() => handleSelectOpp(opp)}
                                            >
                                                <div className="slds-result-icon amber">
                                                    <FontAwesomeIcon icon={faCoins} />
                                                </div>
                                                <div className="slds-result-meta">
                                                    <div className="slds-result-main">
                                                        <span className="slds-result-title">{opp.OpprName}</span>
                                                        <span className="slds-amount-badge">
                                                            ${Number(opp.Amount || 0).toLocaleString('en-US')}
                                                        </span>
                                                        <span className="slds-stage-badge">
                                                            {opp.Stage}
                                                        </span>
                                                    </div>
                                                    <div className="slds-result-sub">
                                                        <span>Account: {opp.AccountName}</span>
                                                        {opp.OwnerName && <span>&bull; Owner: {opp.OwnerName}</span>}
                                                        <span>&bull; Status: {opp.Status}</span>
                                                    </div>
                                                </div>
                                                <button type="button" className="slds-result-action" title="Open Opportunity Details">
                                                    <span>Open</span>
                                                    <FontAwesomeIcon icon={faArrowRight} className="text-11" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* 4. USERS */}
                                {(selectedCategory === 'ALL' || selectedCategory === 'USERS') && results.users.length > 0 && (
                                    <div className="slds-result-group">
                                        <div className="slds-result-group-header">
                                            <FontAwesomeIcon icon={faUsers} className="mr-6px text-secondary" />
                                            Team Members &amp; Users ({results.users.length})
                                        </div>
                                        {results.users.map(u => (
                                            <div
                                                key={`usr-${u.empId}`}
                                                className="slds-result-row"
                                                onClick={handleSelectUser}
                                            >
                                                <div className="slds-result-icon green">
                                                    <FontAwesomeIcon icon={faUser} />
                                                </div>
                                                <div className="slds-result-meta">
                                                    <div className="slds-result-main">
                                                        <span className="slds-result-title">{u.name}</span>
                                                        <span className="slds-user-role-badge">
                                                            {u.designation || (u.RoleID === 99 ? 'Superadmin' : u.RoleID === 98 ? 'Org Admin' : u.RoleID === 97 ? 'Manager' : 'Employee')}
                                                        </span>
                                                    </div>
                                                    <div className="slds-result-sub">
                                                        <span>{u.email}</span>
                                                        {u.department && <span>&bull; {u.department}</span>}
                                                    </div>
                                                </div>
                                                <button type="button" className="slds-result-action" title="View Users">
                                                    <span>Manage</span>
                                                    <FontAwesomeIcon icon={faArrowRight} className="text-11" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                            </div>
                        )}

                    </div>

                    {/* POPOVER FOOTER */}
                    <div className="slds-search-footer">
                        <div className="slds-footer-tip">
                            <span>Press <kbd>Esc</kbd> to close</span>
                        </div>
                        {query.trim() && (
                            <button
                                type="button"
                                className="slds-see-all-btn"
                                onClick={() => {
                                    setIsOpen(false);
                                    navigate('/leads');
                                }}
                            >
                                View full accounts list <FontAwesomeIcon icon={faArrowRight} className="ml-4px" />
                            </button>
                        )}
                    </div>

                </div>
            )}

            {/* MODAL FOR DIRECT ACCOUNT READ VIEW FROM SEARCH */}
            {isAccountModalOpen && modalAccount && (
                <AccountDetailModal
                    isOpen={isAccountModalOpen}
                    account={modalAccount}
                    users={users}
                    onClose={() => setIsAccountModalOpen(false)}
                    onEdit={() => {
                        setIsAccountModalOpen(false);
                        navigate('/leads');
                    }}
                />
            )}
        </div>
    );
}

export default GlobalSearch;
